import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Icon, Button, List, ListItem, ListIcon } from "@chakra-ui/react";
import { FaSolarPanel, FaBuilding, FaBatteryFull, FaChartLine, FaHome, FaTools, FaCheckCircle, FaArrowRight } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SolarServices: React.FC = () => {
  const services = [
    {
      icon: FaHome,
      title: "Residential Solar",
      description: "Complete home solar solutions with premium panels and inverters",
      color: "#C69214",
      bgColor: "white",
      borderColor: "gray.200"
    },
    {
      icon: FaBuilding,
      title: "Commercial Solar",
      description: "Scalable business solutions from small offices to large warehouses",
      color: "#C69214",
      bgColor: "white",
      borderColor: "gray.200"
    },
    {
      icon: FaBatteryFull,
      title: "Battery Storage",
      description: "Energy independence with cutting-edge battery technology",
      color: "#C69214",
      bgColor: "white",
      borderColor: "gray.200"
    },
    {
      icon: FaChartLine,
      title: "Monitoring Systems",
      description: "Real-time energy tracking and performance optimization",
      color: "#C69214",
      bgColor: "white",
      borderColor: "gray.200"
    },
    {
      icon: FaSolarPanel,
      title: "Investment Properties",
      description: "Increase rental appeal and property value with solar",
      color: "#C69214",
      bgColor: "white",
      borderColor: "gray.200"
    },
    {
      icon: FaTools,
      title: "Maintenance & Support",
      description: "Ongoing care and optimization for maximum performance",
      color: "#C69214",
      bgColor: "white",
      borderColor: "gray.200"
    }
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/services"
        fallbackTitle="Solar Services"
        fallbackDescription="Comprehensive solar solutions for residential and commercial properties"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box
        position="relative"
        color="white"
        py={{ base: 16, md: 20 }}
        backgroundImage="url('https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920&q=80')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment={{ base: "scroll", md: "fixed" }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: "rgba(0, 31, 63, 0.85)",
          zIndex: 0
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Heading size="2xl" fontWeight="bold">
              Comprehensive Solar Services
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl">
              From residential installations to commercial projects, we deliver complete solar solutions
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Service Cards Grid */}
      <Container maxW="container.xl" py={16}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
          {services.map((service, index) => (
            <Box
              key={index}
              p={8}
              bg={service.bgColor}
              borderRadius="xl"
              border="2px solid"
              borderColor={service.borderColor}
              _hover={{ transform: "translateY(-8px)", shadow: "2xl" }}
              transition="all 0.3s"
              cursor="pointer"
            >
              <Icon as={service.icon} boxSize={14} color={service.color} mb={4} />
              <Heading size="md" mb={3}>{service.title}</Heading>
              <Text color="gray.600" mb={4}>
                {service.description}
              </Text>
              <Button
                color="#141C49"
                variant="link"
                rightIcon={<FaArrowRight />}
                _hover={{ color: "#a07e10" }}
              >
                Learn More
              </Button>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Why Choose Us Section */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8} mb={12}>
            <Heading size="xl" textAlign="center">Why Choose Our Services?</Heading>
            <Text fontSize="lg" color="gray.600" textAlign="center" maxW="3xl">
              We're committed to delivering exceptional solar solutions backed by expertise and quality
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12}>
            <Box>
              <Heading size="md" mb={6} color="#141C49">
                Quality Assurance
              </Heading>
              <List spacing={4}>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" boxSize={5} />
                  <Text as="span" fontWeight="medium">Tier 1 Premium Panels</Text> - Industry-leading efficiency and warranties
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" boxSize={5} />
                  <Text as="span" fontWeight="medium">CEC Accredited Installers</Text> - Certified professionals on every job
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" boxSize={5} />
                  <Text as="span" fontWeight="medium">25-Year Performance Warranty</Text> - Long-term peace of mind
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" boxSize={5} />
                  <Text as="span" fontWeight="medium">Australian Standards Compliant</Text> - All installations meet AS/NZS standards
                </ListItem>
              </List>
            </Box>

            <Box>
              <Heading size="md" mb={6} color="#141C49">
                Customer Experience
              </Heading>
              <List spacing={4}>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" boxSize={5} />
                  <Text as="span" fontWeight="medium">Free Site Assessment</Text> - No-obligation consultation and quote
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" boxSize={5} />
                  <Text as="span" fontWeight="medium">Transparent Pricing</Text> - Clear quotes with no hidden fees
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" boxSize={5} />
                  <Text as="span" fontWeight="medium">Fast Installation</Text> - Most systems installed within 4-6 weeks
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color="#C69214" boxSize={5} />
                  <Text as="span" fontWeight="medium">Ongoing Support</Text> - Local team available for service and questions
                </ListItem>
              </List>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxW="container.xl" py={16}>
        <Box
          bg="gray.800"
          p={12}
          borderRadius="2xl"
          textAlign="center"
          color="white"
        >
          <Heading size="xl" mb={4}>Ready to Get Started?</Heading>
          <Text fontSize="lg" mb={6} color="gray.300">
            Schedule a free consultation to discuss your solar needs
          </Text>
          <Button size="lg" bg="white" color="#141C49" _hover={{ bg: "#a07e10", color: "white" }} rightIcon={<FaArrowRight />} transition="all 0.2s">
            Book Free Consultation
          </Button>
        </Box>
      </Container>

      <SolarFooter />
    </Box>
  );
};

export default SolarServices;
